/**
 * opd-voice.js — OPD's voice / STT client.
 *
 * Same wire patterns the IPD `Live Capture.html` uses, packaged so the
 * design-system JSX (intake-step-2.jsx, consult-live.jsx) can drop in a
 * single hook and get:
 *
 *   • live waveform from AnalyserNode.getByteFrequencyData()
 *   • mic states (idle / recording / paused / processing / done)
 *   • pulse ring animation hooks (driven by RAF when level > threshold)
 *   • interim transcript ticker (Deepgram WebSocket, `is_final: false`)
 *   • final settle (Deepgram `is_final: true`)
 *   • authenticated batch transcription when WebSocket streaming is
 *     unavailable (including iOS Safari's MP4 recorder)
 *
 * Exposed:
 *
 *   const session = OPDVoice.create({
 *     encounterId, sessionType: 'opd-intake'|'opd-consult',
 *     speaker: 'Doctor'|'Patient'|'Family'|'Child',
 *     onLevel: (n01) => …,          // 0..1, ~60Hz from RAF
 *     onInterim: (text) => …,       // partial transcript
 *     onFinal: (text, meta) => …,   // finalized phrase
 *     onState: (state) => …,        // idle|recording|paused|processing|done|error
 *     onUtteranceEnd: () => …,      // fires on end-of-speech (UtteranceEnd /
 *                                   //   speech_final) — patient paused. Optional;
 *                                   //   does NOT stop the recorder, just signals.
 *     onSilence: () => …,           // alias of onUtteranceEnd (same fire points)
 *     onError: (err) => …,
 *   });
 *
 * onFinal meta CONTRACT (the consult UI reads meta.speaker / meta.role):
 *
 *   {
 *     confidence: number|null,      // Deepgram alternative confidence
 *     speaker:    string,           // role LABEL — 'Doctor'|'Patient'|'Family'|'Child'
 *     role:       string,           // canonical lowercase — 'doctor'|'patient'|'family'|'child'
 *     dgSpeaker:  number|null,      // raw Deepgram diarization index (null if diarization off)
 *     diarized:   boolean,          // true when the label came from Deepgram, not the UI toggle
 *     fallback?:  'authenticated-batch', // present only on batch STT
 *   }
 *
 * Diarization is enabled only on the 'opd-consult' socket (multi-party room:
 * doctor + patient ± family ± child). Intake is a single patient speaking, so
 * it keeps diarize=false. When diarization is on, the FIRST distinct Deepgram
 * speaker index is assumed to be the Doctor (the clinician drives the consult),
 * the second becomes Patient, then Family, then Child. The UI toggle
 * (`opts.speaker`) supplies the default/hint and is used verbatim whenever a
 * final carries no diarization index.
 *
 *   await session.start();
 *   session.pause();
 *   session.resume();
 *   await session.stop();           // returns full transcript + audio meta
 */
import { apiUrl } from './net-base.js';
import { authHeader } from './medplum-client.js';

(function (g) {
  'use strict';

  // ── Deepgram streaming URL builder (mirrors IPD live capture) ───────
  // `diarize` is opt-in: the multi-party consult room turns it on so Deepgram
  // tags each word with a speaker index; single-speaker intake leaves it off.
  function buildDeepgramWsUrl(keyterms, opts) {
    const diarize = !!(opts && opts.diarize);
    // `container` (e.g. 'audio/webm;codecs=opus') is informational: the WS
    // path only ever streams WebM/Opus (the iOS-Safari mp4 case routes to
    // authenticated batch STT instead, never here), so Deepgram detects
    // the container rather than forcing `encoding`/`sample_rate`.
    const params = new URLSearchParams({
      // Keep these in lockstep with IPD `Live Capture.html`.
      // Do not force `encoding` / `sample_rate` here: MediaRecorder sends a
      // WebM/Opus container, and the working IPD path lets Deepgram detect it.
      model: 'nova-3',
      language: 'multi',
      punctuate: 'true',
      smart_format: 'true',
      numerals: 'true',
      diarize: diarize ? 'true' : 'false',
      utterances: 'true',
      interim_results: 'true',
      vad_events: 'true',
      filler_words: 'false',
      profanity_filter: 'false',
      // endpointing=100 per Deepgram's code-switching (language=multi)
      // guidance — 600ms made Hinglish finals land late/lumpy, so this stays
      // low for clean interim/final transcription.
      endpointing: '100',
      // utterance_end_ms is the *silence gap* after which Deepgram declares the
      // patient has actually stopped (it drives UtteranceEnd → onSilenceDetected
      // → auto-advance). Raised 1200→2000: patients pause mid-thought, and at
      // 1.2s the flow decided they were done and jumped pages too eagerly.
      // 2s of quiet is a real "I've finished speaking" signal.
      utterance_end_ms: '2000',
    });
    (keyterms || []).forEach((k) => params.append('keyterm', k));
    return `wss://api.deepgram.com/v1/listen?${params.toString()}`;
  }

  // `surface` selects the server-side keyterm list ('opd-intake' for the
  // kiosk/intake single-speaker flows, 'opd-consult' for the consult room).
  // ttl=60: we used to request ttl=30 but cache the token as "fresh" for
  // 45s, so warm starts handed Deepgram an EXPIRED token → WS rejected →
  // silent REST-only fallback that looked like "slow and inaccurate".
  async function getDeepgramToken(surface, magicLinkToken = '') {
    const q = new URLSearchParams({ ttl: '60' });
    if (surface) q.set('surface', surface);
    const r = await fetch(apiUrl('/api/v1/stt/deepgram-token?' + q.toString()), { headers: voiceCredentialHeaders(magicLinkToken) });
    if (r.status === 503) {
      const e = new Error('STT_NOT_CONFIGURED');
      e.code = 'NOT_CONFIGURED';
      throw e;
    }
    if (!r.ok) throw new Error('deepgram-token failed: ' + r.status);
    return r.json();
  }

  function voiceCredentialHeaders(magicLinkToken = '') {
    const headers = { ...authHeader() };
    if (magicLinkToken) headers['X-Healther-Magic-Link'] = magicLinkToken;
    return headers;
  }

  async function transcribeRecordedBlob(audioBlob, { magicLinkToken = '', blobType = '', sessionType = 'opd-intake', speaker = 'Patient' } = {}) {
    const isMp4 = /mp4/i.test(blobType || audioBlob?.type || '');
    const form = new FormData();
    form.append('audio', audioBlob, isMp4 ? 'capture.mp4' : 'capture.webm');
    const response = await fetch(apiUrl('/api/v1/stt/transcribe'), {
      method: 'POST',
      headers: voiceCredentialHeaders(magicLinkToken),
      body: form,
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(body.error || `batch transcription failed: ${response.status}`);
      error.code = 'STT_BATCH_FAILED';
      error.status = response.status;
      throw error;
    }
    return {
      transcript: String(body.transcript || '').trim(),
      durationMs: Number(body.durationMs) || 0,
      confidence: Number.isFinite(body.confidence) ? body.confidence : null,
      sessionType,
      speaker,
    };
  }

  // ── Near-duplicate transcript dedup (mirrors IPD Live Capture) ────
  // Deepgram occasionally re-finalizes the SAME audio with a corrected
  // word (an ASR retry on a drug name, e.g. "Pentacidin" → "penicillin").
  // Streaming then emits the sentence twice. We merge such adjacent,
  // same-speaker finals so the transcript — and the extractor — see one
  // clinical event, not two. The merge keeps the LONGER text (Deepgram's
  // later finals are usually more complete), so no words are lost.
  function normalizeForDup(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/[.,!?;:'"()\[\]]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }
  function tokensOf(s) {
    return normalizeForDup(s).split(' ').filter(Boolean);
  }
  // Pull out numeric tokens (doses, vitals, frequencies). If two lines
  // carry DIFFERENT numbers we never merge them — "paracetamol 500" and
  // "paracetamol 650" are distinct clinical facts, not an ASR retry.
  function numbersOf(s) {
    const m = normalizeForDup(s).match(/\d+(?:\.\d+)?/g);
    return m ? m.slice().sort() : [];
  }
  function sameNumbers(a, b) {
    const na = numbersOf(a), nb = numbersOf(b);
    if (na.length !== nb.length) return false;
    return na.every((v, i) => v === nb[i]);
  }
  function isNearDuplicate(prev, next) {
    const a = normalizeForDup(prev);
    const b = normalizeForDup(next);
    if (!a || !b) return false;
    // OPD safety guard: different numbers ⇒ never a duplicate.
    if (!sameNumbers(prev, next)) return false;
    // 1) exact match after normalization.
    if (a === b) return true;
    // 2) substring containment when either side is reasonably long
    //    (catches truncated stale finals mid-sentence).
    if ((a.length >= 12 || b.length >= 12) && (a.includes(b) || b.includes(a))) return true;
    // 3) token Jaccard ≥ 0.7 on the full strings.
    const ta = new Set(tokensOf(a));
    const tb = new Set(tokensOf(b));
    if (!ta.size || !tb.size) return false;
    let inter = 0;
    ta.forEach((t) => { if (tb.has(t)) inter++; });
    const union = ta.size + tb.size - inter;
    return union > 0 && inter / union >= 0.7;
  }

  // ── Speaker diarization → clinical role ──────────────────────────
  // Deepgram (diarize=true) tags each word with an integer `speaker` index
  // in the order it first hears a new voice (0, 1, 2, …). Those indices are
  // session-relative and carry no inherent meaning, so we map them to clinical
  // roles in first-heard order with a sensible default: the clinician drives
  // the consult and almost always speaks first, so speaker 0 → Doctor, then
  // Patient, then Family, then Child. Any further speakers fall back to Family
  // (extra relatives in the room are clinically attributed to the patient).
  //
  // ROLE_LABEL is the human-facing label the UI bubbles show; the canonical
  // lowercase role is what downstream code keys on.
  const ROLE_ORDER = ['doctor', 'patient', 'family', 'child'];
  const ROLE_LABEL = { doctor: 'Doctor', patient: 'Patient', family: 'Family', child: 'Child' };

  // Normalize a UI-supplied speaker hint ('Doctor'|'Patient'|'Family'|'Child',
  // any case) to a canonical role. Defaults to 'patient' for unknown values so
  // a stray label never silently becomes a clinician.
  function roleFromLabel(label) {
    const k = String(label || '').trim().toLowerCase();
    if (ROLE_LABEL[k]) return k;            // already canonical
    if (k.includes('doctor') || k.includes('dr ') || k.startsWith('dr')) return 'doctor';
    if (k.includes('family') || k.includes('relative') || k.includes('attendant') || k.includes('caregiver')) return 'family';
    if (k.includes('child') || k.includes('kid') || k.includes('son') || k.includes('daughter')) return 'child';
    if (k.includes('patient')) return 'patient';
    return 'patient';
  }

  // Per-session diarization→role resolver. `defaultRole` is the UI toggle's
  // role, used when a final has no diarization index. The first distinct
  // Deepgram index seen is pinned to that default (so if the doctor opened the
  // mic as 'Doctor', speaker 0 → Doctor); subsequent new indices walk the role
  // order, skipping roles already assigned.
  function createRoleResolver(defaultRole) {
    const byIndex = new Map();          // dgSpeaker index → canonical role
    const used = new Set();
    let firstSeen = false;
    function claim(role) {
      // If the preferred role is taken, fall to the next free slot, then Family.
      if (!used.has(role)) { used.add(role); return role; }
      for (const r of ROLE_ORDER) { if (!used.has(r)) { used.add(r); return r; } }
      return 'family';
    }
    return {
      // Resolve an utterance's dominant Deepgram speaker index to a role.
      resolve(dgIndex) {
        if (dgIndex == null || Number.isNaN(dgIndex)) {
          return defaultRole || 'patient';
        }
        if (byIndex.has(dgIndex)) return byIndex.get(dgIndex);
        let role;
        if (!firstSeen) {
          // First voice heard is assumed to be whoever holds the mic (the
          // doctor, by default) — pin it to the UI toggle's role.
          role = claim(defaultRole || 'doctor');
          firstSeen = true;
        } else {
          role = claim(ROLE_ORDER[byIndex.size] || 'family');
        }
        byIndex.set(dgIndex, role);
        return role;
      },
    };
  }

  // Dominant Deepgram speaker index across a final's words. Deepgram puts a
  // per-word `speaker` int on alt.words[] when diarize=true; an utterance can
  // straddle a turn boundary, so we pick the most-spoken index, not just [0].
  function dominantSpeaker(words) {
    if (!Array.isArray(words) || !words.length) return null;
    const counts = new Map();
    for (const w of words) {
      const s = (w && typeof w.speaker === 'number') ? w.speaker : null;
      if (s == null) continue;
      counts.set(s, (counts.get(s) || 0) + 1);
    }
    if (!counts.size) return null;
    let best = null, bestN = -1;
    for (const [s, n] of counts) { if (n > bestN) { best = s; bestN = n; } }
    return best;
  }

  // ── AnalyserNode → RAF level loop ────────────────────────────────
  function attachAnalyser(stream, onLevel) {
    const AC = g.AudioContext || g.webkitAudioContext;
    if (!AC) return { stop() {} };
    const ctx = new AC();
    const src = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    src.connect(analyser);
    const data = new Uint8Array(analyser.frequencyBinCount);
    let raf;
    let alive = true;
    function tick() {
      if (!alive) return;
      analyser.getByteFrequencyData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) sum += data[i];
      const avg = sum / data.length / 255;
      try { onLevel && onLevel(Math.min(1, avg * 1.6)); } catch (e) {}
      raf = g.requestAnimationFrame(tick);
    }
    tick();
    return {
      stop() {
        alive = false;
        cancelAnimationFrame(raf);
        try { src.disconnect(); } catch (e) {}
        try { ctx.close(); } catch (e) {}
      },
    };
  }

  // ── Session ──────────────────────────────────────────────────────
  function create(opts = {}) {
    const o = Object.assign({
      encounterId: null,
      sessionType: 'opd-intake',
      speaker: 'Patient',
      lang: (g.OPDBridge?.lang) || 'en',
      dedup: true,           // merge Deepgram's re-finalized near-duplicates
      // Diarization defaults ON for the multi-party consult room and OFF for
      // single-speaker intake. Callers may override with an explicit boolean.
      diarize: undefined,
      onLevel: () => {},
      onInterim: () => {},
      onFinal: () => {},
      onState: () => {},
      onReady: () => {},     // fires once the Deepgram socket reaches OPEN
      // End-of-speech signal. Deepgram already emits UtteranceEnd (after
      // utterance_end_ms of silence) and tags a final with `speech_final` when
      // its own endpointer decides the speaker paused. We surface BOTH as a
      // single "the patient stopped talking" callback so callers (intake
      // auto-advance) can react. Optional + backward compatible: defaults to a
      // no-op, and we never auto-stop the recorder here — this only signals.
      onUtteranceEnd: () => {},
      onSilence: () => {},   // alias of onUtteranceEnd (same fire points)
      onError: () => {},
    }, opts);

    // Resolve whether this session diarizes. Honour an explicit opts.diarize;
    // otherwise enable it for the consult session type only.
    const diarizeOn = (typeof o.diarize === 'boolean')
      ? o.diarize
      : (o.sessionType === 'opd-consult');
    // The UI toggle's role anchors the diarization map (speaker 0 → this role).
    const defaultRole = roleFromLabel(o.speaker);
    let roleResolver = createRoleResolver(defaultRole);

    let state = 'idle';
    let stream = null;
    let recorder = null;
    let ws = null;
    let analyser = null;
    let chunks = [];
    let finalSegments = [];
    let pendingInterim = '';
    let useStreaming = true;
    let startedAtMs = 0;
    let wsOpened = false;
    // The MediaRecorder container actually negotiated at start() time. WebM/Opus
    // on Chrome/Android/desktop; 'audio/mp4' on iOS Safari (which can't do
    // WebM). Used to name the authenticated batch blob correctly.
    let recContainer = 'audio/webm;codecs=opus';
    // Audio chunks captured before the Deepgram socket reaches OPEN. We
    // start the recorder immediately (so the first words are never lost)
    // and flush these, in order, the moment the socket opens. The first
    // chunk carries the WebM container header, so order is preserved.
    let preOpenQueue = [];
    let lastFinalMs = 0;     // wall-clock of the most recent committed final
    // Monotonic run id. start() captures it; the async token→WebSocket hop
    // checks it before opening the socket so a stop()/reset() that lands
    // mid-connect can't resurrect a socket for a dead session.
    let runId = 0;
    // Set by stop() when it lands BEFORE start() has created a recorder. The
    // in-flight start() checks it after each await (getUserMedia, recorder
    // creation) and bails — so tapping Stop during the 'connecting' phase
    // aborts the start instead of leaving a live mic + dead session behind.
    let cancelled = false;
    // While stop() is draining Deepgram, this resolves the wait as soon as
    // the `from_finalize` final (or UtteranceEnd) arrives — instead of the
    // old fixed 800ms sleep that cut off slower flushes (trailing words).
    let drainResolve = null;

    // Keyterm surface for the token endpoint: intake flows get the curated
    // Hinglish-symptom list, the consult room keeps the clinical-drug list.
    const surface = o.surface || (o.sessionType === 'opd-consult' ? 'opd-consult' : 'opd-intake');

    function setState(s) { state = s; try { o.onState(s); } catch (e) {} }

    // Fire the end-of-speech signal to any interested caller. Best-effort and
    // idempotent-by-intent: each natural pause fires once (UtteranceEnd OR a
    // speech_final, not both for the same pause — see signalledThisPause).
    // Never throws, never touches the recorder; it's a pure notification.
    let signalledThisPause = false;
    function signalEndOfSpeech() {
      try { o.onUtteranceEnd(); } catch (e) {}
      try { o.onSilence(); } catch (e) {}
    }

    // Commit a dangling interim as a final segment. Deepgram sometimes never
    // re-emits interim words as a final (stop mid-utterance, slow flush) —
    // previously that displayed text was silently DISCARDED. Called on
    // UtteranceEnd and at the end of the stop() drain. Runs the same
    // near-duplicate guard as streamed finals so we never double-commit
    // words a real final already covered.
    function commitPendingInterim() {
      const t = (pendingInterim || '').trim();
      pendingInterim = '';
      if (!t) return;
      const nowMs = Date.now();
      const role = defaultRole;
      const speakerLabel = ROLE_LABEL[role] || o.speaker;
      const prev = finalSegments[finalSegments.length - 1];
      if (
        o.dedup && prev &&
        prev.role === role &&
        (nowMs - lastFinalMs) <= 6000 &&
        isNearDuplicate(prev.text, t)
      ) {
        if (t.length > prev.text.length) prev.text = t;
        lastFinalMs = nowMs;
        return;
      }
      finalSegments.push({ text: t, ts: new Date().toISOString(), conf: null, speaker: speakerLabel, role, dgSpeaker: null, diarized: false });
      lastFinalMs = nowMs;
      try { o.onFinal(t, { confidence: null, speaker: speakerLabel, role, dgSpeaker: null, diarized: false }); } catch (e) {}
    }

    // ─── prewarm ──────────────────────────────────────────────────
    // Open the microphone + cache a Deepgram token BEFORE the user taps
    // record. The patient triggers this implicitly by landing on Step 1,
    // which calls `session.prewarm()` once on mount. After that the
    // first record tap reuses the warm `stream`/`tokenCache`, dropping
    // the visible `connecting` phase from ~1.3s → <250ms.
    //
    // Safe to call repeatedly: subsequent invocations are no-ops while
    // the stream is alive. We deliberately don't open the WebSocket
    // here — Deepgram closes idle sockets in ~10s, which would defeat
    // the purpose; instead we fetch the token (which IS cacheable)
    // and let `start()` open the WS at the exact moment we need it.
    let warmStream = null;
    let warmToken = null;
    let warmTokenAt = 0;
    async function prewarm() {
      if (state !== 'idle') return false;
      // Secure-context guard: a phone on a raw LAN http origin has no
      // navigator.mediaDevices, so getUserMedia would throw on mount. Fail
      // soft (don't flip to 'error' — prewarm is best-effort); the visible
      // banner is surfaced later when the user actually taps Record.
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return false;
      let ok = true;
      try {
        if (!warmStream) {
          warmStream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
        }
      } catch (e) {
        // Permission not granted yet — fail soft. The user will see the
        // real prompt on the first tap. Don't flip state to 'error' for
        // a pre-warm failure; the UI shouldn't show a red anything.
        ok = false;
      }
      try {
        // Cache the Deepgram short-lived token (minted with ttl=60). Refresh
        // once it's older than 40s so surfaces that call prewarm() on an
        // interval (~40s) always hold a token with comfortable life left —
        // start() treats anything ≤50s old as fresh.
        if (!warmToken || (Date.now() - warmTokenAt) > 40000) {
          warmToken = await getDeepgramToken(surface, o.token);
          warmTokenAt = Date.now();
        }
      } catch (e) {
        ok = false;
      }
      return ok;
    }

    async function start() {
      if (state !== 'idle') return false;
      // Secure-context guard: getUserMedia only exists over https or
      // localhost. A phone opened on a raw LAN IP (http) has
      // navigator.mediaDevices === undefined, so the call below would throw.
      // Surface a tagged error the UI shows as a visible banner, and SOFT-FAIL
      // (return false) so the caller's catch + onState('error') recovery runs.
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setState('error');
        try { o.onError(Object.assign(new Error('INSECURE_CONTEXT: mic needs https or localhost'), { code: 'INSECURE_CONTEXT' })); } catch (e) {}
        return false;
      }
      // No MediaRecorder at all (very old / locked-down WebView) — fail soft
      // with a clear error instead of throwing on `new MediaRecorder` below.
      if (typeof MediaRecorder === 'undefined') {
        setState('error');
        try { o.onError(Object.assign(new Error('MEDIARECORDER_UNSUPPORTED: this browser can\'t record audio'), { code: 'MEDIARECORDER_UNSUPPORTED' })); } catch (e) {}
        return false;
      }
      const myRun = ++runId;
      cancelled = false;
      setState('processing');
      startedAtMs = Date.now();

      // Resolve the Deepgram token CONCURRENTLY with the mic request. The
      // old flow was fully serial (mic → token → WS → second mic → IndexedDB
      // → record): audio capture started 0.5–1.7s after the tap and the
      // patient's first words were NEVER captured. Reuse a prewarmed token
      // when it's still fresh — tokens are minted with ttl=60, so anything
      // ≤50s old is safe (the old 45s window against ttl=30 handed Deepgram
      // EXPIRED tokens, silently dropping to REST-only).
      const tokenPromise = (async () => {
        const warm = g.OPDVoice && g.OPDVoice.__warmToken;
        const fresh = warm && (Date.now() - warm.at) < 50000 ? warm.tok : null;
        const localFresh = (warmToken && (Date.now() - warmTokenAt) < 50000) ? warmToken : null;
        const tok = fresh || localFresh || await getDeepgramToken(surface, o.token);
        // Single-use: clear after consuming so the next session re-fetches.
        if (fresh && g.OPDVoice) g.OPDVoice.__warmToken = null;
        return tok;
      })();
      // Swallow here; the .then() chain below owns failure handling. Without
      // this, a token failure while we await the mic is an unhandled rejection.
      tokenPromise.catch(() => {});

      try {
        // Reuse the pre-warmed mic stream if one is alive. Otherwise
        // request now — which prompts for permission the first time.
        if (warmStream && warmStream.active) {
          stream = warmStream;
          warmStream = null;
        } else {
          stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
        }
      } catch (e) {
        // SOFT fail: flip to 'error' (the single recovery path the caller's
        // onState handler keys on) and return false so the caller's catch
        // fires too. Never leave a dead session behind.
        setState('error');
        try { o.onError(Object.assign(new Error('Microphone permission denied: ' + e.message), { code: 'MIC_DENIED' })); } catch (_) {}
        return false;
      }
      // stop()/reset() landed while we were awaiting the mic — abort cleanly
      // so we never flip a cancelled session to 'recording'.
      if (cancelled || runId !== myRun) {
        try { recorder?.stop(); } catch (e) {}
        stream?.getTracks().forEach((t) => t.stop());
        return false;
      }
      analyser = attachAnalyser(stream, o.onLevel);

      // MediaRecorder feeds Deepgram WS when available and retains the full
      // in-memory blob for the authenticated batch path when it is not.
      // iOS Safari can't record WebM/Opus — probe for a supported container
      // and fall back to mp4 (batch only; Deepgram's WS path needs
      // webm/opus). `recContainer` is passed to the batch blob below.
      const webmOk = (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported)
        ? MediaRecorder.isTypeSupported('audio/webm;codecs=opus') : false;
      const mp4Ok = (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported)
        ? MediaRecorder.isTypeSupported('audio/mp4') : false;
      recContainer = webmOk ? 'audio/webm;codecs=opus' : (mp4Ok ? 'audio/mp4' : '');
      // Deepgram's streaming WS only handles the WebM/Opus container the IPD
      // path uses. When the device can't produce it (iOS Safari → mp4), skip
      // the socket and go straight to authenticated batch STT on stop().
      if (!webmOk) useStreaming = false;
      try {
        recorder = recContainer
          ? new MediaRecorder(stream, { mimeType: recContainer })
          : new MediaRecorder(stream);
      } catch (e) {
        try { recorder = new MediaRecorder(stream); } catch (e2) {
          // SOFT fail — see the getUserMedia catch above.
          setState('error');
          try { o.onError(Object.assign(new Error('MediaRecorder unsupported: ' + e2.message), { code: 'MEDIARECORDER_UNSUPPORTED' })); } catch (_) {}
          stream?.getTracks().forEach((t) => t.stop());
          return false;
        }
      }
      // Cancelled while creating the recorder — bail before any chunks flow.
      if (cancelled || runId !== myRun) {
        try { recorder?.stop(); } catch (e) {}
        stream?.getTracks().forEach((t) => t.stop());
        return false;
      }

      recorder.ondataavailable = (ev) => {
        if (!ev.data || ev.data.size === 0) return;
        chunks.push(ev.data);
        if (useStreaming) {
          ev.data.arrayBuffer().then((buf) => {
            if (ws && ws.readyState === WebSocket.OPEN) {
              try { ws.send(buf); } catch (e) {}
            } else if (!wsOpened) {
              // Socket still connecting (or token still in flight) — buffer
              // so the first words aren't lost; flushed in ws.onopen.
              preOpenQueue.push(buf);
            }
          });
        }
      };

      // Guard the recording flip behind the run id: a stop()/reset() that
      // raced this start() bumps runId, so a late onstart can't resurrect a
      // dead session into 'recording'.
      recorder.onstart = () => { if (runId === myRun && !cancelled) setState('recording'); };
      recorder.onpause = () => setState('paused');
      recorder.onresume = () => setState('recording');
      recorder.onstop = () => { /* stop handled in stop() */ };

      // Start capturing IMMEDIATELY — do not wait for the token or socket.
      // Chunks produced before the socket opens are buffered in preOpenQueue
      // (see ondataavailable) and flushed on ws.onopen, so the first words
      // are never dropped. 250ms timeslice keeps the interim ticker responsive.
      // Guarded behind the run id so a stop() that landed mid-connect can't
      // start a recorder for an already-dead session.
      if (runId === myRun && !cancelled) {
        recorder.start(250);
      } else {
        try { recorder.stop(); } catch (e) {}
        stream?.getTracks().forEach((t) => t.stop());
        return false;
      }

      // Open the Deepgram socket the moment the token arrives.
      tokenPromise.then((tok) => {
        // Session was stopped/reset while the token was in flight, or this
        // browser records MP4 and must use batch STT instead of the WebM WS.
        if (!useStreaming || runId !== myRun || !recorder) return;
        ws = new WebSocket(buildDeepgramWsUrl(tok.keyterms, { diarize: diarizeOn, container: recContainer }), ['bearer', tok.access_token]);
        ws.binaryType = 'arraybuffer';
        ws.onopen = () => {
          wsOpened = true;
          // Flush any audio captured before the socket opened, in order.
          if (preOpenQueue.length) {
            const queued = preOpenQueue;
            preOpenQueue = [];
            for (const buf of queued) {
              try { ws.send(buf); } catch (e) {}
            }
          }
          try { o.onReady(); } catch (e) {}
        };
        ws.onmessage = (ev) => {
          try {
            const m = JSON.parse(ev.data);
            if (m.type === 'UtteranceEnd') {
              // End-of-speech marker. If the last words only ever arrived as
              // an interim (no final), commit them now — the old handler
              // matched type==='Results' only, so dangling interims were
              // silently dropped. Also ends the stop() drain wait early.
              commitPendingInterim();
              if (drainResolve) drainResolve();
              // Surface the end-of-speech signal to the caller (intake
              // auto-advance). UtteranceEnd fires after utterance_end_ms of
              // silence with no further audio, so it's the authoritative
              // "patient paused" marker. Fire once per pause.
              if (!signalledThisPause) { signalledThisPause = true; signalEndOfSpeech(); }
              return;
            }
            if (m.type === 'Results') {
              // Finalize ack: Deepgram tags the flushed final with
              // `from_finalize` (often an EMPTY transcript when nothing was
              // buffered). It ends the stop() drain wait — after we commit
              // any transcript it carries.
              const isFlushAck = !!(m.is_final && m.from_finalize);
              const alt = m.channel?.alternatives?.[0];
              const t = ((alt && alt.transcript) || '').trim();
              if (!t) {
                if (isFlushAck && drainResolve) drainResolve();
                return;
              }
              // Real transcript content (interim or final) means the patient is
              // speaking again — re-arm the pause signal so the NEXT silence
              // fires onUtteranceEnd. Skip the finalize-ack (it carries no new
              // speech, just drains the socket).
              if (!isFlushAck) signalledThisPause = false;
              if (m.is_final) {
                const nowMs = Date.now();
                // Diarization → role. When the consult socket diarizes,
                // Deepgram tags alt.words[] with a per-word speaker index; we
                // map the utterance's dominant index to a clinical role. With
                // diarization off (intake) or no index present, fall back to
                // the UI toggle's role/label.
                const dgSpeaker = diarizeOn ? dominantSpeaker(alt.words) : null;
                const role = diarizeOn ? roleResolver.resolve(dgSpeaker) : defaultRole;
                const speakerLabel = ROLE_LABEL[role] || o.speaker;
                const diarized = diarizeOn && dgSpeaker != null;
                // Near-duplicate merge: if Deepgram re-finalized the same
                // utterance (same ROLE, within 6s, near-identical text), keep
                // the longer version instead of emitting a 2nd bubble. We key
                // on resolved role — never merge a doctor line into a patient
                // line just because the words happened to be similar.
                const prev = finalSegments[finalSegments.length - 1];
                if (
                  o.dedup && prev &&
                  prev.role === role &&
                  (nowMs - lastFinalMs) <= 6000 &&
                  isNearDuplicate(prev.text, t)
                ) {
                  if (t.length > prev.text.length) prev.text = t;
                  if (alt.confidence != null) prev.conf = alt.confidence;
                  lastFinalMs = nowMs;
                  pendingInterim = '';
                  if (isFlushAck && drainResolve) drainResolve();
                  return; // suppress duplicate onFinal
                }
                finalSegments.push({ text: t, ts: new Date().toISOString(), conf: alt.confidence ?? null, speaker: speakerLabel, role, dgSpeaker, diarized });
                lastFinalMs = nowMs;
                pendingInterim = '';
                try { o.onFinal(t, { confidence: alt.confidence, speaker: speakerLabel, role, dgSpeaker, diarized }); } catch (e) {}
                // speech_final is Deepgram's own end-of-utterance verdict on a
                // final (its endpointer heard a pause). Treat it as a fallback
                // end-of-speech signal for the rare case UtteranceEnd doesn't
                // land — but NOT during the stop() drain (the flush ack), where
                // there's no live "next step" to advance to. Guarded by
                // signalledThisPause so we never double-fire with UtteranceEnd.
                if (m.speech_final && !isFlushAck && !signalledThisPause) {
                  signalledThisPause = true;
                  signalEndOfSpeech();
                }
                if (isFlushAck && drainResolve) drainResolve();
              } else {
                pendingInterim = t;
                try { o.onInterim(t); } catch (e) {}
              }
            }
          } catch (e) {}
        };
        ws.onerror = (e) => {
          // Fall back to authenticated batch STT if the socket never opens.
          if (!wsOpened) useStreaming = false;
          else o.onError(new Error('Deepgram websocket error'));
        };
        ws.onclose = () => { ws = null; };
      }).catch(() => {
        // Token mint failed / streaming not available → the recorder keeps
        // buffering and stop() uses authenticated batch STT on the full blob.
        useStreaming = false;
      });
    }

    function pause() {
      if (recorder && recorder.state === 'recording') recorder.pause();
    }
    function resume() {
      if (recorder && recorder.state === 'paused') recorder.resume();
    }

    async function stop() {
      if (!recorder) {
        // No recorder yet means a start() may still be in flight (mic prompt
        // or recorder creation pending). Signal it to abort: flip `cancelled`,
        // bump runId so its post-await guards bail, and tear down anything it
        // already opened (the warm stream / a stream from a racing start).
        cancelled = true;
        runId++;
        try { if (warmStream) warmStream.getTracks().forEach((t) => t.stop()); } catch (e) {}
        warmStream = null;
        try { if (stream) stream.getTracks().forEach((t) => t.stop()); } catch (e) {}
        stream = null;
        if (state !== 'idle') setState('idle');
        return { transcript: '', durationMs: 0, segments: [], audioBlob: null, audioUrl: null };
      }
      runId++; // invalidate any in-flight token→WebSocket hop from start()
      setState('processing');
      // Drain the recorder.
      await new Promise((resolve) => {
        recorder.onstop = () => resolve();
        try { recorder.stop(); } catch (e) { resolve(); }
      });
      // Drain Deepgram: send Finalize and WAIT for the `from_finalize` final
      // (or UtteranceEnd) instead of a fixed 800ms sleep — the flush often
      // takes longer and the old timer cut off the speaker's trailing words.
      // Capped at 2s so a dead socket can't hang the patient's Done tap.
      // Only drain when the socket ACTUALLY opened (wsOpened) — a Stop tapped
      // during the 'connecting' phase has nothing buffered to flush, so we
      // close immediately instead of waiting out the 2s cap.
      if (wsOpened && ws && ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify({ type: 'Finalize' })); } catch (e) {}
        await new Promise((resolve) => {
          const cap = setTimeout(() => { drainResolve = null; resolve(); }, 2000);
          drainResolve = () => { clearTimeout(cap); drainResolve = null; resolve(); };
        });
        try { ws.close(); } catch (e) {}
      } else if (ws) {
        // Never opened (still CONNECTING) or already closing — close so the
        // late-opening socket can't linger, and resolve instantly.
        try { ws.close(); } catch (e) {}
      }
      // Anything still showing as an interim never got a final from Deepgram
      // — commit it instead of discarding the words the user watched appear.
      commitPendingInterim();
      // Stop analyser & mic.
      if (analyser) analyser.stop();
      if (stream) stream.getTracks().forEach((t) => { try { t.stop(); } catch (e) {} });

      // If streaming didn't yield any finals (or it never connected), use
      // authenticated batch transcription on the buffered blob.
      let transcript = finalSegments.map((s) => s.text).join(' ').trim();
      let durationMs = Math.max(0, Date.now() - startedAtMs);
      const blobType = chunks.length ? (chunks[0].type || recContainer || 'audio/webm') : 'audio/webm';
      const audioBlob = chunks.length ? new Blob(chunks, { type: blobType }) : null;
      const audioUrl = audioBlob ? URL.createObjectURL(audioBlob) : null;
      let transcriptionError = null;
      if (!transcript && audioBlob) {
        try {
          const batch = await transcribeRecordedBlob(audioBlob, {
            magicLinkToken: o.token,
            blobType,
            sessionType: o.sessionType,
            speaker: o.speaker,
          });
          transcript = batch.transcript;
          durationMs = batch.durationMs || durationMs;
          if (transcript) try { o.onFinal(transcript, { confidence: batch.confidence, speaker: o.speaker, role: defaultRole, dgSpeaker: null, diarized: false, fallback: 'authenticated-batch' }); } catch (e) {}
        } catch (error) {
          transcriptionError = error;
          setState('error');
          try { o.onError(error); } catch (e) {}
        }
      }

      if (!transcriptionError) setState('done');
      const out = {
        transcript,
        durationMs,
        segments: finalSegments,
        transcriptionError,
        audioBlob,
        audioUrl,
      };
      // Reset for next use
      recorder = null; ws = null; analyser = null; stream = null; chunks = []; finalSegments = []; pendingInterim = ''; startedAtMs = 0; state = 'idle';
      preOpenQueue = []; lastFinalMs = 0; wsOpened = false; useStreaming = true; drainResolve = null;
      cancelled = false; recContainer = 'audio/webm;codecs=opus';
      signalledThisPause = false;
      // Fresh diarization map for the next session — speaker indices are
      // session-relative and must not leak across recordings.
      roleResolver = createRoleResolver(defaultRole);
      return out;
    }

    // Explicit fresh-start: wipe all in-memory capture state so the next
    // recording begins on a blank canvas (e.g. after a consult is signed
    // and the doctor records a new note). Refuses while a capture is live
    // unless `force` — callers persist via stop() first, so nobody has to
    // re-speak. Returns true if it reset.
    function reset({ force = false } = {}) {
      if (!force && (state === 'recording' || state === 'paused')) return false;
      // The runId bump below is what aborts any in-flight start(): its
      // post-await guards bail on runId mismatch. (We don't latch `cancelled`
      // here because reset() is synchronous and clears it again below; runId
      // is the durable signal the async start() actually checks.)
      runId++;
      try { if (analyser) analyser.stop(); } catch (e) {}
      try { if (stream) stream.getTracks().forEach((t) => t.stop()); } catch (e) {}
      try { if (warmStream) warmStream.getTracks().forEach((t) => t.stop()); } catch (e) {}
      warmStream = null;
      try { if (ws && ws.readyState === WebSocket.OPEN) ws.close(); } catch (e) {}
      recorder = null; ws = null; analyser = null; stream = null;
      chunks = []; finalSegments = []; pendingInterim = '';
      preOpenQueue = []; lastFinalMs = 0; startedAtMs = 0;
      wsOpened = false; useStreaming = true; drainResolve = null;
      cancelled = false; recContainer = 'audio/webm;codecs=opus';
      signalledThisPause = false;
      roleResolver = createRoleResolver(defaultRole);
      setState('idle');
      return true;
    }

    return {
      start, pause, resume, stop, prewarm, reset,
      get state() { return state; },
      get pendingInterim() { return pendingInterim; },
    };
  }

  g.OPDVoice = { create, buildDeepgramWsUrl, getDeepgramToken, transcribeRecordedBlob, isNearDuplicate, roleFromLabel, createRoleResolver, dominantSpeaker, ROLE_LABEL, ROLE_ORDER };
})(typeof window !== 'undefined' ? window : globalThis);
